import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Frown, Meh, Smile, Laugh, Heart, ArrowUpRight } from "lucide-react";
import { voteMood, track } from "@/lib/analytics";
import { useLang } from "@/lib/i18n";
import { SENTIMENT_REACTIONS, GUIDE, CASE_STUDIES } from "@/data/portfolio";
import GuideIcon from "@/components/site/GuideIcon";

export const MOOD_ICONS = [Frown, Meh, Smile, Laugh, Heart];
const LABELS = ["Unconvinced", "Meh", "Interested", "Impressed", "Hire her"];
const VOTED_KEY = "sn_mood_votes";

export const readVotes = () => JSON.parse(localStorage.getItem(VOTED_KEY) || "{}");

export default function MoodVote({ stage, stats, onMoods, onNavigate }) {
  const { t } = useLang();
  const [votes, setVotes] = useState(readVotes);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const mine = votes[stage];
  const reaction = mine === 1 ? SENTIMENT_REACTIONS[stage] : null;
  const reactionCase = reaction && CASE_STUDIES.find((c) => c.slug === reaction.slug);

  const vote = async (m) => {
    if (busy) return;
    setBusy(true);
    const next = { ...votes, [stage]: m };
    localStorage.setItem(VOTED_KEY, JSON.stringify(next));
    setVotes(next);
    track("mood_vote", { stage, mood: m });
    if (m === 1) track("sentiment_nudge", { stage, slug: SENTIMENT_REACTIONS[stage]?.slug });
    try {
      onMoods(await voteMood(stage, m));
    } catch {
      /* keep local vote */
    }
    setBusy(false);
  };

  return (
    <div data-testid="mood-vote" className="mt-5 p-4 bg-white border border-[#e5e0d8] rounded-xl">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="font-mono-label text-[9px] text-[#86928a]">
          {mine ? t("Your mood at this stage, tap to change") : t("How do you feel at this stage?")}
        </div>
        {stats?.count > 0 && (
          <div data-testid="mood-stats" className="font-mono-label text-[9px] text-[#9a7218]">
            {t("live avg")} {stats.avg.toFixed(1)} / 5 · {stats.count} {stats.count === 1 ? t("vote") : t("votes")}
          </div>
        )}
      </div>
      {reaction && (
        <div
          data-testid="sentiment-reaction"
          className="mt-3 p-3 rounded-xl bg-[#14352a] text-[#faf8f5] flex items-start gap-3 fade-up"
        >
          <span className="h-8 w-8 shrink-0 rounded-full btn-guide grid place-items-center">
            <GuideIcon className="h-5 w-5" />
          </span>
          <div className="min-w-0">
            <div className="font-mono-label text-[8px] text-[#d9a92e]">{GUIDE.name} {t("reacts")}</div>
            <p data-testid="sentiment-reaction-text" className="mt-1 text-sm leading-relaxed text-[#faf8f5]/90">{t(reaction.text)}</p>
            <button
              data-testid="sentiment-reaction-cta"
              onClick={() => {
                track("sentiment_nudge_click", { stage, slug: reaction.slug });
                onNavigate?.();
                navigate(`/case-studies/${reaction.slug}`);
              }}
              className="mt-2 inline-flex items-center gap-1 font-mono-label text-[9px] text-[#d9a92e] link-underline"
            >
              {t("Show me:")} {t(reactionCase?.label)} <ArrowUpRight className="h-3 w-3" />
            </button>
          </div>
        </div>
      )}
      <div className="mt-3 flex gap-2">
        {MOOD_ICONS.map((Icon, i) => {
          const m = i + 1;
          const active = mine === m;
          return (
            <button
              key={m}
              data-testid={`mood-btn-${m}`}
              onClick={() => vote(m)}
              title={t(LABELS[i])}
              aria-label={t(LABELS[i])}
              aria-pressed={active}
              className={`group flex-1 flex flex-col items-center gap-1 py-2 rounded-lg border transition-all ${
                active
                  ? "border-[#d9a92e] bg-[#fff1f0] scale-105"
                  : "border-[#e5e0d8] hover:border-[#9a7218] hover:-translate-y-0.5"
              }`}
            >
              <Icon className={`h-5 w-5 ${active ? "text-[#d9a92e]" : "text-[#14352a] group-hover:text-[#9a7218]"}`} />
              <span className="text-[8px] sm:text-[9px] text-[#86928a] leading-none whitespace-nowrap">{t(LABELS[i])}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
